import { useCallback, useState } from 'react';

import { BottomPanelKey } from 'types';

export type BottomSheetProps = {
  active?: boolean;
};

export type BottomSheetState<ContentKey extends unknown, Params extends unknown> = {
  isOpen: boolean;
  contentKey: ContentKey | null;
  params?: Params | null;
};

export type BottomSheetResult<ContentKey, Params> = {
  state: BottomSheetState<ContentKey, Params>;
  open(
    contentKey: ContentKey,
    options?: Pick<BottomSheetState<ContentKey, Params>, 'params'> & {
      reversible?: boolean;
    }
  ): void;
  close(): void;
};

const useBottomSheet = <ContentKey extends BottomPanelKey, Params extends object = {}>({
  active = true
}: BottomSheetProps = {}): BottomSheetResult<ContentKey, Params> => {
  const [state, setState] = useState<BottomSheetState<ContentKey, Params>>({
    isOpen: false,
    contentKey: null,
    params: null
  });

  const open = useCallback(
    (
      contentKey: ContentKey,
      options?: Pick<BottomSheetState<ContentKey, Params>, 'params'> & { reversible?: boolean }
    ) => {
      if (!active) return;

      setState(prev => {
        if (options?.reversible && prev.isOpen && prev.contentKey === contentKey) {
          return { isOpen: false, contentKey: null };
        }

        return { isOpen: true, contentKey, ...options };
      });
    },
    [active]
  );

  const close = useCallback(() => {
    setState(prev => ({
      ...prev,
      isOpen: false,
      contentKey: null
    }));
  }, []);

  return {
    state,
    open,
    close
  };
};

export default useBottomSheet;
